import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { adminService, type StorageHealthResponse } from '@/shared/api/admin-service';
import { useAuthStore } from '@/features/auth/model/auth-store';

const REFRESH_MS = 60_000;

export type HealthSeverity = 'green' | 'amber' | 'red';

/**
 * Polls /admin/storage/health every 60s. Disabled for non-admin users so
 * non-admins don't pay for a useless query.
 *
 * Returns the raw payload + a derived overall severity used by both the
 * page (per-row colors) and the global banner (show/hide).
 */
export const useStorageHealth = () => {
  const isPlatformAdmin = useAuthStore(
    (state) => state.user?.platformRole === 'platform_admin',
  );

  const query = useQuery<StorageHealthResponse>({
    queryKey: ['admin', 'storage-health'],
    queryFn: () => adminService.getStorageHealth(),
    enabled: isPlatformAdmin,
    refetchInterval: REFRESH_MS,
    // Background polling — keep stale data visible during refetch so we don't
    // flash the skeleton every 60s.
    refetchIntervalInBackground: false,
    staleTime: REFRESH_MS / 2,
    retry: 1,
  });

  const severity = useMemo<HealthSeverity>(() => deriveSeverity(query.data, query.isError), [
    query.data,
    query.isError,
  ]);

  return { ...query, severity, isPlatformAdmin };
};

/**
 * Pure, exported for unit-testing.
 *
 * - Endpoint failure → red (we can't verify, treat as failed)
 * - Required bucket missing OR isPublic flag wrong → red
 * - Audit-log error count > 0 in last hour → red
 * - Lifecycle 'unknown' OR proxy counts null → amber (informational)
 * - Otherwise → green
 */
export function deriveSeverity(
  data: StorageHealthResponse | undefined,
  isError: boolean,
): HealthSeverity {
  if (isError) return 'red';
  if (!data) return 'amber'; // loading / no data yet

  const privateOk = data.buckets.aurousPrivate.exists && !data.buckets.aurousPrivate.isPublic;
  const publicOk =
    data.buckets.aurousPublic.exists && data.buckets.aurousPublic.isPublic;
  const uploadErrorsOk = data.recent.adminAssetUploadErrors1h === 0;

  if (!privateOk || !publicOk || !uploadErrorsOk) return 'red';

  // Lifecycle 'unknown' is a known limitation, not a failure — surface as amber
  // so admins know to verify in Supabase, but we don't paint the banner red on it.
  if (data.lifecycle.refsTtlConfigured !== 'yes') return 'amber';

  return 'green';
}
